import { useMemo, useState } from 'react';
import { ArrowDown2, ArrowUp2 } from 'iconsax-reactjs';
import { timeframeShort, type Timeframe } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { useTheme } from '@/lib/useTheme';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { TimeframeButtons } from '@/components/chart/TimeframeButtons';
import { VolumeChart } from '@/components/chart/VolumeChart';
import { chartPalette } from '@/components/chart/chartTheme';
import type { ProfileDrawing } from '@/components/chart/volumePrimitive';
import { VolumeIcon } from '@/components/VolumeIcon';
import { ModuleWeight } from '@/components/ModuleWeight';
import { useVolumeProfile, type LevelKind, type ProfileLevel } from './api';

/**
 * Bin counts offered.
 *
 * Resolution is a real choice rather than a setting to get right once. Coarse
 * bins show the shape of acceptance; fine bins resolve the thin shelves price
 * raced through, at the cost of turning ordinary noise into apparent structure.
 */
const BIN_PRESETS = [36, 72, 120];

const KIND_LABEL: Record<LevelKind, string> = {
  poc: 'volume.poc',
  vah: 'volume.vah',
  val: 'volume.val',
  lvn: 'volume.lvn',
  hvn: 'volume.hvn',
};

export function VolumePage() {
  const { theme } = useTheme();
  const palette = chartPalette(theme);
  const { symbol, isLoading: symbolLoading } = useSymbol();
  const { t } = useI18n();

  const [timeframe, setTimeframe] = useState<Timeframe>('H1');
  const [bins, setBins] = useState(72);

  const query = useVolumeProfile(symbol, timeframe, { bins });
  const profile = query.data?.profile ?? null;
  const digits = query.data?.digits ?? 2;
  const candles = query.data?.candles ?? [];
  const price = candles.length > 0 ? candles[candles.length - 1]!.close : null;

  const drawing = useMemo<ProfileDrawing | null>(() => {
    if (!profile) return null;
    return {
      bins: profile.bins.map((b) => ({
        low: b.low,
        high: b.high,
        volume: b.volume,
        inValueArea: b.inValueArea,
      })),
      /*
       * Only the structural levels are drawn, not every ranked one.
       *
       * The list beside the chart is ordered by what to watch; the chart's job
       * is to show the shape those readings came from. Drawing ten lines over a
       * histogram makes both harder to read.
       */
      lines: [
        { price: profile.poc, kind: 'poc' as const, label: `POC ${profile.poc.toFixed(digits)}` },
        { price: profile.vah, kind: 'vah' as const, label: `VAH ${profile.vah.toFixed(digits)}` },
        { price: profile.val, kind: 'val' as const, label: `VAL ${profile.val.toFixed(digits)}` },
        ...profile.levels
          .filter((l) => l.kind === 'lvn')
          .slice(0, 3)
          .map((l) => ({
            price: l.price,
            kind: 'lvn' as const,
            label: `LVN ${l.price.toFixed(digits)}`,
          })),
      ],
      width: 0.28,
      colors: {
        bar: palette.volumeBar,
        barValue: palette.volumeValue,
        poc: palette.volumePoc,
        edge: palette.volumeEdge,
        lvn: palette.volumeLvn,
        hvn: palette.volumeHvn,
        label: palette.text,
      },
    };
  }, [profile, digits, palette]);

  const inValueArea =
    profile !== null && price !== null && price >= profile.val && price <= profile.vah;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <VolumeIcon size={22} className="text-primary" />
          {t('volume.title')}
        </h1>
        <ModuleWeight id="volume" />
        <span className="tabular ms-auto text-sm text-muted-foreground">{symbol ?? '—'}</span>
      </div>
      <p className="max-w-2xl text-sm text-muted-foreground">{t('volume.intro')}</p>

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        {/* min-w-0 so the chart column can shrink after full screen sizes it. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-1">
            <TimeframeButtons value={timeframe} onChange={setTimeframe} />
            <div className="ms-auto flex items-center gap-1">
              {BIN_PRESETS.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBins(b)}
                  aria-pressed={bins === b}
                  className={cn(
                    'rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                    bins === b
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-elevated text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t('volume.rows', { n: b })}
                </button>
              ))}
            </div>
          </div>

          <ChartFrame
            label={t('volume.chart')}
            fullscreenToolbar={<TimeframeButtons value={timeframe} onChange={setTimeframe} compact />}
            className="group/frame h-[34rem] overflow-hidden glass rounded-2xl"
          >
            {query.isPending || symbolLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : candles.length === 0 || !profile ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('volume.noData', { timeframe: timeframeShort(timeframe) })}
              </div>
            ) : (
              <VolumeChart
                className="h-full w-full"
                candles={candles}
                drawing={drawing}
                digits={digits}
                theme={theme}
                resetKey={`${symbol}-${timeframe}`}
              />
            )}
          </ChartFrame>

          {profile && (
            <p className="text-xs text-muted-foreground">
              {t('volume.caption', {
                bars: profile.bars.toLocaleString(),
                timeframe: timeframeShort(timeframe),
                rows: profile.bins.length,
                size: profile.binSize.toFixed(digits),
                pct: Math.round(profile.valueAreaPct * 100),
              })}
            </p>
          )}
        </section>

        <aside className="order-1 space-y-3 lg:order-2">
          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2 text-sm font-medium">{t('volume.structure')}</h2>
            {profile ? (
              <dl className="space-y-1.5 text-xs">
                <Row label={t('volume.vah')} value={profile.vah.toFixed(digits)} />
                <Row label={t('volume.poc')} value={profile.poc.toFixed(digits)} accent />
                <Row label={t('volume.val')} value={profile.val.toFixed(digits)} />
                <Row
                  label={t('volume.price')}
                  value={price !== null ? price.toFixed(digits) : '—'}
                />
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">{t('volume.noData', { timeframe: timeframeShort(timeframe) })}</p>
            )}
            {profile && (
              <p className="mt-2 border-t border-border/60 pt-2 text-[11px] leading-snug text-muted-foreground">
                {t(inValueArea ? 'volume.insideNote' : 'volume.outsideNote')}
              </p>
            )}
          </div>

          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2 text-sm font-medium">{t('volume.reversalLevels')}</h2>
            {!profile || profile.levels.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('volume.noLevels')}</p>
            ) : (
              <ul className="space-y-1">
                {profile.levels.map((l) => (
                  <LevelRow key={`${l.kind}:${l.price}`} level={l} digits={digits} />
                ))}
              </ul>
            )}
            <p className="mt-2 border-t border-border/60 pt-2 text-[11px] leading-snug text-muted-foreground">
              {t('volume.scoreNote')}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('tabular font-medium', accent && 'text-primary')}>{value}</dd>
    </div>
  );
}

function LevelRow({ level, digits }: { level: ProfileLevel; digits: number }) {
  const { t } = useI18n();
  const up = level.side === 'above';

  return (
    <li className="flex items-center gap-2 rounded-lg bg-elevated/40 px-2 py-1.5">
      <span
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded',
          up ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
        )}
      >
        {up ? <ArrowUp2 size={12} variant="Bold" /> : <ArrowDown2 size={12} variant="Bold" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="tabular block truncate text-xs font-medium">{level.price.toFixed(digits)}</span>
        <span className="block truncate text-[10px] text-muted-foreground">
          {t(KIND_LABEL[level.kind] as never)} ·{' '}
          {level.distance >= 0 ? '+' : ''}
          {level.distance.toFixed(digits)}
        </span>
      </span>

      {/* The score as a bar, because the ordering is the point and a column of
          three-decimal numbers hides it. */}
      <span className="h-1.5 w-12 shrink-0 overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full rounded-full bg-primary"
          style={{ width: `${Math.round(level.score * 100)}%` }}
        />
      </span>
    </li>
  );
}
